import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  loadBook,
  getChapterTitle,
  getChapterChoiceText,
} from "../services/bookService";
import { sendFeedbackEmail } from "../services/emailService";
import { Book, Chapter, ChapterHistoryEntry } from "../types/Book";
import PreviewFeedbackForm from "./PreviewFeedbackForm";
import OptimizedImage from "./OptimizedImage";
import TrailerPlayer from "./TrailerPlayer";
import ChapterNavigation from "./ChapterNavigation";
import { chapterProgressService } from "../services/chapterProgressService";
import analytics from "../services/analytics";
import "../styles/BookReader.css";

interface PathChoice {
  chapterFile: string;
  title: string;
}

const BookReader: React.FC = () => {
  const { bookId } = useParams<{ bookId: string }>();
  const navigate = useNavigate();

  const [book, setBook] = useState<Book | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Chapter state
  const [currentChapter, setCurrentChapter] = useState<Chapter | null>(null);
  const [currentChapterTitle, setCurrentChapterTitle] = useState<string>("");
  const [availablePaths, setAvailablePaths] = useState<PathChoice[]>([]);
  
  // Preview feedback state
  const [showPreviewFeedback, setShowPreviewFeedback] = useState(false);
  const [completedPaths, setCompletedPaths] = useState<string[]>([]);
  
  // Audio state for controlling animations
  const [audioState, setAudioState] = useState<{isPlaying: boolean, hasStopped: boolean, hasStartedPlaying: boolean}>({
    isPlaying: false,
    hasStopped: false,
    hasStartedPlaying: false
  });

  // Chapter history for back navigation
  const [chapterHistory, setChapterHistory] = useState<ChapterHistoryEntry[]>(
    []
  );
  const [canNavigateBack, setCanNavigateBack] = useState(false);

  // Chapter navigation state
  const [showChapterNavigation, setShowChapterNavigation] = useState(false);


  // Analytics tracking refs to prevent duplicates
  const hasTrackedBookEntryRef = useRef(false);
  const hasTrackedFirstChapterRef = useRef(false);

  // Load book data
  useEffect(() => {
    // Reset analytics tracking flags when bookId changes
    hasTrackedBookEntryRef.current = false;
    hasTrackedFirstChapterRef.current = false;

    const fetchBook = async () => {
      if (!bookId) return;

      try {
        setLoading(true);
        const loadedBook = await loadBook(bookId);
        if (loadedBook) {
          setBook(loadedBook);
          
          // Track book reader entry (once per session)
          if (!hasTrackedBookEntryRef.current) {
            analytics.trackBookReaderEntered(
              bookId, 
              loadedBook.title, 
              document.referrer.includes(window.location.origin) ? 'homepage' : 'direct_link'
            );
            hasTrackedBookEntryRef.current = true;
          }
          
          // Load first chapter (01.md)
          const firstChapter = loadedBook.chapters.find(
            (ch) => ch.file === "01.md"
          );
          if (firstChapter) {
            setCurrentChapter(firstChapter);
            const title = await getChapterTitle(bookId, firstChapter.file);
            setCurrentChapterTitle(title);
            
            // Track chapter visit in progress service
            chapterProgressService.updateChapterProgress(bookId, firstChapter.file, {});
            
            // Track first chapter start (once per session)
            if (!hasTrackedFirstChapterRef.current) {
              analytics.trackChapterStarted(bookId, firstChapter.file, title, true);
              hasTrackedFirstChapterRef.current = true;
            }
          }
          setError(null);
        } else {
          setError("Book not found");
        }
      } catch (err) {
        console.error("Error loading book:", err);
        setError("Failed to load book");
      } finally {
        setLoading(false);
      }
    };

    fetchBook();
  }, [bookId]);

  // Load available path choices when audio ends
  const loadAvailablePaths = useCallback(async () => {
    if (!currentChapter || !book || !bookId) return;

    // Check if this is a preview endpoint chapter
    const isPreviewEnd = book.preview_mode && 
                         book.preview_chapters && 
                         book.preview_chapters.includes(parseInt(currentChapter.file.replace('.md', '')));

    if (isPreviewEnd) {
      // Track preview endpoint reached
      const currentPathName = currentChapter.path_name || 'main';
      analytics.trackPreviewEndpointReached(
        bookId, 
        currentPathName, 
        chapterHistory.length + 1 // Include current chapter
      );
      
      // Show feedback form instead of path choices
      setShowPreviewFeedback(true);
      return;
    }

    const pathChoices: PathChoice[] = [];

    for (const pathChapterFile of currentChapter.paths) {
      const chapterFile = `${pathChapterFile}.md`;
      const title = await getChapterChoiceText(bookId, chapterFile);
      pathChoices.push({
        chapterFile,
        title,
      });
    }

    // Track story choices presented
    if (pathChoices.length > 0) {
      analytics.trackStoryChoicePresented(
        bookId,
        currentChapter.file,
        pathChoices.map(choice => choice.title)
      );
    }

    setAvailablePaths(pathChoices);
  }, [currentChapter, book, bookId]);

  // Handle audio ended
  const handleAudioEnded = useCallback(() => {
    loadAvailablePaths();
  }, [loadAvailablePaths]);

  // Save current chapter to history
  const saveToHistory = useCallback(
    (
      chapter: Chapter,
      title: string,
      choiceText?: string
    ) => {
      const historyEntry: ChapterHistoryEntry = {
        chapterFile: chapter.file,
        chapterTitle: title,
        choiceText,
        timestamp: Date.now(),
      };

      setChapterHistory((prev) => {
        const newHistory = [...prev, historyEntry];
        // Keep only last 15 entries to prevent memory bloat
        return newHistory.slice(-15);
      });
      setCanNavigateBack(true);
    },
    []
  );

  // Handle back navigation
  const handleGoBack = useCallback(async () => {
    if (chapterHistory.length === 0 || !book) return;

    // Get the previous chapter from history
    const previousEntry = chapterHistory[chapterHistory.length - 1];
    const previousChapter = book.chapters.find(
      (ch) => ch.file === previousEntry.chapterFile
    );

    if (previousChapter && currentChapter) {
      // Track back navigation
      analytics.trackChapterBackNavigation(
        bookId || '',
        currentChapter.file,
        previousChapter.file
      );

      // Remove the last entry from history
      setChapterHistory((prev) => {
        const newHistory = prev.slice(0, -1);
        setCanNavigateBack(newHistory.length > 0);
        return newHistory;
      });

      // Set the previous chapter
      setCurrentChapter(previousChapter);
      setCurrentChapterTitle(previousEntry.chapterTitle);
      setAvailablePaths([]);
    }
  }, [chapterHistory, book, bookId, currentChapter]);

  // Handle path selection
  const handlePathSelect = async (pathChoice: PathChoice) => {
    if (!book || !currentChapter) return;

    // Track story path selection
    analytics.trackStoryPathSelected(
      book.id,
      currentChapter.file,
      pathChoice.chapterFile,
      pathChoice.title
    );

    // Save current chapter to history before moving to next
    saveToHistory(currentChapter, currentChapterTitle, pathChoice.title);

    const nextChapter = book.chapters.find(
      (ch) => ch.file === pathChoice.chapterFile
    );
    if (nextChapter) {
      setCurrentChapter(nextChapter);
      const title = await getChapterTitle(book.id, nextChapter.file);
      setCurrentChapterTitle(title);
      setAvailablePaths([]);
      
      // Track chapter visit in progress service
      chapterProgressService.updateChapterProgress(book.id, nextChapter.file, {});
      
      // Track new chapter start
      analytics.trackChapterStarted(book.id, nextChapter.file, title, false);
    }
  };


  const handleBackToHome = () => {
    navigate("/");
  };

  // Handle chapter selection from navigation
  const handleChapterSelect = async (chapterFile: string) => {
    if (!book) return;

    const selectedChapter = book.chapters.find(ch => ch.file === chapterFile);
    if (!selectedChapter) return;

    // Save current chapter to history if it's different from the selected one
    if (currentChapter && currentChapter.file !== chapterFile) {
      saveToHistory(currentChapter, currentChapterTitle);
    }

    // Set the new chapter
    setCurrentChapter(selectedChapter);
    const title = await getChapterTitle(book.id, selectedChapter.file);
    setCurrentChapterTitle(title);
    setAvailablePaths([]);

    // Track chapter visit in progress service
    chapterProgressService.updateChapterProgress(book.id, selectedChapter.file, {});

    // Track chapter navigation
    analytics.trackChapterStarted(book.id, selectedChapter.file, title, false);
  };

  // Handle feedback submission
  const handleFeedbackSubmit = async (feedbackData: any) => {
    console.log('Feedback submitted:', feedbackData);
    
    // Track the completed path
    const currentPathName = currentChapter?.path_name || 'main';
    if (currentPathName && !completedPaths.includes(currentPathName)) {
      setCompletedPaths(prev => [...prev, currentPathName]);
    }

    // Track feedback submission
    analytics.trackFeedbackSubmitted(
      book?.id || '',
      currentPathName,
      feedbackData.rating,
      feedbackData.feedback?.length
    );

    try {
      // Send feedback email to support@voy-gr.com
      await sendFeedbackEmail(feedbackData);
      console.log('Feedback email sent successfully');
    } catch (error) {
      console.error('Failed to send feedback email:', error);
      // Note: We still let the form show success since the user experience 
      // shouldn't be disrupted if email fails
    }
  };

  // Handle trying another path
  const handleTryAnotherPath = () => {
    // Reset to chapter 1 to let user choose a different path
    if (book) {
      const firstChapter = book.chapters.find(ch => ch.file === "01.md");
      if (firstChapter) {
        setCurrentChapter(firstChapter);
        setShowPreviewFeedback(false);
        setAvailablePaths([]);
        // Clear chapter history for fresh start
        setChapterHistory([]);
        setCanNavigateBack(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="book-reader">
        <div className="loading-message">
          <p>Loading book...</p>
        </div>
      </div>
    );
  }

  if (error || !book) {
    return (
      <div className="book-reader">
        <div className="error-message">
          <p>{error || "Book not found"}</p>
          <button onClick={handleBackToHome}>← Back to Library</button>
        </div>
      </div>
    );
  }

  return (
    <div className="book-reader">
      <header className="reader-header fade-in">
        <div className="header-left">
          <button className="back-button" onClick={handleBackToHome}>
            Back to Library
          </button>
          {canNavigateBack && !showPreviewFeedback && (
            <button className="chapter-back-button" onClick={handleGoBack}>
              Previous Chapter
            </button>
          )}
          {!showPreviewFeedback && (
            <button 
              className={`chapter-nav-button ${showChapterNavigation ? 'active' : ''}`}
              onClick={() => setShowChapterNavigation(!showChapterNavigation)}
              aria-label="Toggle chapter navigation"
            >
              ☰
            </button>
          )}
        </div>
        <div className="book-info">
          <h1>{book.title}</h1>
          <p>{currentChapterTitle}</p>
        </div>
      </header>

      <main className="reader-content">
        <div className="chapter-display">
          {/* Show feedback form if this is a preview endpoint */}
          {showPreviewFeedback ? (
            <PreviewFeedbackForm
              bookId={book.id}
              pathName={currentChapter?.path_name || 'main'}
              completedPaths={completedPaths}
              onSubmit={handleFeedbackSubmit}
              onTryAnotherPath={handleTryAnotherPath}
              onBackToHome={handleBackToHome}
            />
          ) : (
            <>
              {currentChapter && availablePaths.length === 0 && (
                <div className="chapter-illustration">
                  {currentChapter.illustration ? (
                    <OptimizedImage
                      src={currentChapter.illustration}
                      alt={currentChapterTitle}
                      lazy={false}
                      priority={true}
                      className="chapter-image"
                    />
                  ) : (
                    <div className="chapter-image no-illustration">
                      {showChapterNavigation ? (
                        <ChapterNavigation
                          book={book}
                          currentChapter={currentChapter}
                          onChapterSelect={handleChapterSelect}
                          onClose={() => setShowChapterNavigation(false)}
                          isOpen={true}
                          chapterHistory={chapterHistory}
                        />
                      ) : (
                        <div className="playing-graphic">
                          <div className="playing-icon">
                            <div className="sound-wave">
                              <div className={`wave-bar ${audioState.isPlaying ? '' : 'paused'}`}></div>
                              <div className={`wave-bar ${audioState.isPlaying ? '' : 'paused'}`}></div>
                              <div className={`wave-bar ${audioState.isPlaying ? '' : 'paused'}`}></div>
                              <div className={`wave-bar ${audioState.isPlaying ? '' : 'paused'}`}></div>
                            </div>
                          </div>
                          <p className="playing-text">
                            {audioState.isPlaying ? 'Playing...' : 
                             audioState.hasStopped ? 'Stopped' :
                             audioState.hasStartedPlaying && !audioState.isPlaying ? 'Paused' : 'Loading...'}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {availablePaths.length > 0 && (
                <div className="audio-visualization">
                  <p className="audio-status">
                    {availablePaths.length === 1 ? "Continue:" : "Choose your path:"}
                  </p>
                </div>
              )}

              {availablePaths.length > 0 && (
                <div className="path-choices fade-in">
                  <div className="path-buttons">
                    {availablePaths.map((pathChoice, index) => (
                      <button
                        key={index}
                        className="path-button slide-in-up"
                        style={{ animationDelay: `${index * 0.1}s` }}
                        onClick={() => handlePathSelect(pathChoice)}
                      >
                        {pathChoice.title}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Simple audio controls using TrailerPlayer */}
      {!showPreviewFeedback && currentChapter?.audio && (
        <footer className="reader-footer">
          <TrailerPlayer
            bookId={bookId || ''}
            audioFile={currentChapter.audio}
            bookTitle={book?.title || ''}
            chapterTitle={currentChapterTitle}
            onAudioEnded={handleAudioEnded}
            onAudioStateChange={setAudioState}
          />
        </footer>
      )}

    </div>
  );
};

export default BookReader;
